import {domToReact, HTMLReactParserOptions, Element as ParserElement, DOMNode} from 'html-react-parser';
import {useContext} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, ClientDynamicContent, Icon, NodeElement} from 'components';
import {
    DynamicComponentNames,
    WaitingForAgentWaitingForAgentUnits as Units,
    WAITING_FOR_AGENT_DEFAULT_TEMPLATE
} from 'constants/client';
import {DATE_FORMAT_CALL} from 'constants/format';
import {ClientContext} from 'context';
import {useClientSession} from 'hooks';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {dynamicContentUtils} from 'utils/dynamic-content';
import {formatDate} from 'utils/transform';

import {WaitingAgentResponse} from '../WaitingAgentResponse';

import './styles.scss';

const T_KEYS_CLIENT_IN_SESSION_PAGE = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE;
const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.WAITING_FOR_AGENT_VIEW;

const {isMatchElement, nodeElementParent, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {
        title, hasSchedule, scheduleDate, scheduleDateLabel, onLeaveSession, leaveSessionLabel,
        localParticipant
    } = props;

    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;
            if (isMatchElement(Units.Icon, node)) {
                return <NodeElement node={node}><WaitingAgentResponse /></NodeElement>;
            }
            if (isMatchElement(Units.Title, node)) {
                return <NodeElement node={node}>{title}</NodeElement>;
            }
            if (isMatchElement(Units.Schedule, node)) {
                return nodeElementParent({show: hasSchedule});
            }
            if (isMatchElement(Units.ScheduleDateLabel, node)) {
                return <NodeElement node={node}>{scheduleDateLabel}</NodeElement>;
            }
            if (isMatchElement(Units.ScheduleDate, node)) {
                return <NodeElement node={node}>{scheduleDate}</NodeElement>;
            }
            if (isMatchElement(Units.LeaveSessionButton, node)) {
                return (
                    <NodeElement node={node} onClick={onLeaveSession}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.LeaveSessionButtonDefault, node)) {
                return (
                    <NodeElement node={node}>
                        <Button
                            color='attention'
                            width='auto'
                            height='auto'
                            rounded
                            onClick={onLeaveSession}
                        >
                            <Icon name='phone-end' size='4xl' />
                        </Button>
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.LeaveSessionButtonLabel, node)) {
                return (
                    <NodeElement node={node}>{leaveSessionLabel}</NodeElement>
                );
            }

            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

export const WaitingForAgentView: React.FC = () => {
    const {t} = useTranslation();
    const {endLocalSession} = useContext(ClientContext);
    const {image, timeZone, scheduleStartDate} = useClientSession();

    return (
        <ClientDynamicLayout image={image}>
            <ClientDynamicContent
                component={DynamicComponentNames.WaitingForAgent}
                defaultTemplate={WAITING_FOR_AGENT_DEFAULT_TEMPLATE}
                optionsProps={{
                    title: t(T_KEYS.TITLE),
                    hasSchedule: !!scheduleStartDate,
                    scheduleDate: formatDate(scheduleStartDate, timeZone, DATE_FORMAT_CALL),
                    scheduleDateLabel: t(T_KEYS.SCHEDULE),
                    leaveSessionLabel: t(T_KEYS_CLIENT_IN_SESSION_PAGE.END_CALL),
                    onLeaveSession: endLocalSession
                }}
                optionsGetter={getOptions}
            />
        </ClientDynamicLayout>
    );
};
